import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Stack,
  Menu,
  alpha,
  IconButton,
} from "@mui/material";
import {
  People as ReferralIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
  KeyboardArrowDown as ArrowDownIcon,
  CheckCircle as ActiveIcon,
  HowToReg as RegisteredIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const SummaryTile = ({ label, value, color, icon }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 2,
      border: `1px solid ${alpha(color, 0.28)}`,
      background: `linear-gradient(145deg, ${alpha(color, 0.1)} 0%, #fff 70%)`,
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      minHeight: 72,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        display: "grid",
        placeItems: "center",
        bgcolor: alpha(color, 0.14),
        color,
        border: `1px solid ${alpha(color, 0.22)}`,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: crmColors.text.secondary,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "1.35rem", color: crmColors.text.primary, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const ReferralManagement = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFinancialYearId, setSelectedFinancialYearId] = useState("");
  const [fyAnchor, setFyAnchor] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null); // { monthNumber, year } or null = all

  const [schools, setSchools] = useState([]);
  const [staff, setStaff] = useState([]);
  const [unmappedSchools, setUnmappedSchools] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [summary, setSummary] = useState({
    registered_schools: 0,
    active_schools: 0,
    unmapped_schools: 0,
    referring_staff: 0,
    total_referrals_all: 0,
  });

  const [schoolSearch, setSchoolSearch] = useState("");
  const [staffSearch, setStaffSearch] = useState("");
  const [selectedStaffCode, setSelectedStaffCode] = useState("");

  const [mapOpen, setMapOpen] = useState(false);
  const [mapSchool, setMapSchool] = useState(null);
  const [mapCode, setMapCode] = useState("");
  const [mapSearch, setMapSearch] = useState("");

  const baseYear = useMemo(() => {
    if (!selectedFinancialYearId) return new Date().getFullYear();
    const fy = financialYears.find((item) => {
      const id = item.financial_year_id || item.year_id || item.id || item.year;
      return String(id) === String(selectedFinancialYearId);
    });
    if (fy?.start_date) {
      const y = Number(String(fy.start_date).slice(0, 4));
      if (!Number.isNaN(y)) return y;
    }
    if (fy?.financial_year_name) {
      const match = String(fy.financial_year_name).match(/\d{4}/);
      if (match) return Number(match[0]);
    }
    const n = Number(selectedFinancialYearId);
    return Number.isNaN(n) ? new Date().getFullYear() : n;
  }, [selectedFinancialYearId, financialYears]);

  //Created By Cursor — Apr–Mar FY months (parity with Schools)
  const financialYearMonths = useMemo(() => {
    const months = [];
    for (let i = 4; i <= 12; i += 1) months.push({ monthNumber: i, year: baseYear });
    for (let i = 1; i <= 3; i += 1) months.push({ monthNumber: i, year: baseYear + 1 });
    return months;
  }, [baseYear]);

  const yearMonth = useMemo(() => {
    if (!selectedMonth) return "";
    const m = String(selectedMonth.monthNumber).padStart(2, "0");
    return `${selectedMonth.year}-${m}`;
  }, [selectedMonth]);

  const selectedFyLabel = useMemo(() => {
    if (!selectedFinancialYearId) return "Session";
    const fy = financialYears.find((item) => {
      const id = item.financial_year_id || item.year_id || item.id || item.year;
      return String(id) === String(selectedFinancialYearId);
    });
    return (
      fy?.financial_year_name ||
      fy?.name ||
      String(selectedFinancialYearId)
    );
  }, [financialYears, selectedFinancialYearId]);

  const loadFinancialYears = useCallback(async () => {
    const result = await apiService.getFinancialYearList();
    if (!result.success) {
      toast.error(result.error || "Failed to load sessions");
      return;
    }
    const list = Array.isArray(result.data) ? result.data : [];
    setFinancialYears(list);
    const current =
      list.find((fy) => String(fy.status || "").toLowerCase() === "current") || list[0];
    if (current) {
      const id = String(
        current.financial_year_id || current.year_id || current.id || ""
      );
      setSelectedFinancialYearId((prev) => prev || id);
    }
    const now = new Date();
    setSelectedMonth((prev) => {
      if (prev) return prev;
      return { monthNumber: now.getMonth() + 1, year: now.getFullYear() };
    });
  }, []);

  const fetchDashboard = useCallback(async () => {
    if (!selectedFinancialYearId) return;
    setLoading(true);
    const result = await apiService.getReferralDashboard({
      financial_year_id: selectedFinancialYearId,
      year_month: yearMonth,
    });
    if (result.success && result.data) {
      setSchools(Array.isArray(result.data.schools) ? result.data.schools : []);
      setStaff(Array.isArray(result.data.staff) ? result.data.staff : []);
      setUnmappedSchools(
        Array.isArray(result.data.unmapped_schools) ? result.data.unmapped_schools : []
      );
      setStaffOptions(
        Array.isArray(result.data.staff_options) ? result.data.staff_options : []
      );
      if (result.data.summary) {
        setSummary({
          registered_schools: Number(result.data.summary.registered_schools) || 0,
          active_schools: Number(result.data.summary.active_schools) || 0,
          unmapped_schools: Number(result.data.summary.unmapped_schools) || 0,
          referring_staff: Number(result.data.summary.referring_staff) || 0,
          total_referrals_all: Number(result.data.summary.total_referrals_all) || 0,
        });
      }
    } else {
      toast.error(result.error || "Failed to load referral data");
      setSchools([]);
      setStaff([]);
      setUnmappedSchools([]);
    }
    setLoading(false);
  }, [selectedFinancialYearId, yearMonth]);

  useEffect(() => {
    loadFinancialYears();
  }, [loadFinancialYears]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const filteredSchools = useMemo(() => {
    let rows = schools;
    if (selectedStaffCode) {
      rows = rows.filter(
        (r) =>
          String(r.referal_code || "").toUpperCase() ===
          String(selectedStaffCode).toUpperCase()
      );
    }
    const q = schoolSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.school_name,
        r.school_code,
        r.admin_name,
        r.admin_phone,
        r.referal_code,
        r.referrer_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [schools, schoolSearch, selectedStaffCode]);

  const filteredStaff = useMemo(() => {
    const q = staffSearch.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter((r) => {
      const hay = [r.staff_name, r.referal_code, r.staff_phone, r.staff_role]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [staff, staffSearch]);

  const mapCandidates = useMemo(() => {
    const q = mapSearch.trim().toLowerCase();
    let rows = unmappedSchools;
    if (q) {
      rows = rows.filter((r) => {
        const hay = [r.school_name, r.school_code, r.admin_phone, r.admin_name]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return rows.slice(0, 100);
  }, [unmappedSchools, mapSearch]);

  const openMapDialog = (school = null) => {
    setMapSchool(school);
    setMapCode(school?.referal_code || "");
    setMapSearch(school ? "" : "");
    setMapOpen(true);
  };

  const handleMapReferral = async () => {
    const schoolCode = mapSchool?.school_code;
    if (!schoolCode) {
      toast.error("Select a school");
      return;
    }
    if (!mapCode.trim()) {
      toast.error("Select a referral code");
      return;
    }
    setSaving(true);
    const result = await apiService.mapSchoolReferral({
      school_code: schoolCode,
      referal_code: mapCode.trim(),
    });
    setSaving(false);
    if (result.success) {
      toast.success(
        result.data?.referrer_name
          ? `Mapped to ${result.data.referrer_name}`
          : "Referral mapped"
      );
      setMapOpen(false);
      setMapSchool(null);
      fetchDashboard();
    } else {
      toast.error(result.error || "Failed to map referral");
    }
  };

  const handleCopy = (code) => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    toast.success("Code copied");
  };

  const barSubtitle = selectedMonth
    ? `${MONTH_SHORT[selectedMonth.monthNumber - 1]} ${selectedMonth.year} · ${selectedFyLabel}`
    : `${selectedFyLabel} · all months`;

  return (
    <PageContentLayout
      title="Referral Management"
      subtitle="Session schools registered by referral · staff performance · map missing codes"
      darkBar
      barSx={{
        background: `linear-gradient(120deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.start} 55%, ${crmColors.hero.mid} 100%)`,
      }}
      barSubtitle={barSubtitle}
      breadcrumbs={[
        {
          label: "Referral Management",
          icon: <ReferralIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
      ]}
      backTo="/dashboard"
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="contained"
            disableElevation
            endIcon={<ArrowDownIcon />}
            startIcon={<CalendarIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => setFyAnchor(e.currentTarget)}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "10px",
              height: 36,
              bgcolor: alpha("#fff", 0.16),
              color: "#fff",
              border: `1px solid ${alpha("#fff", 0.35)}`,
              "&:hover": { bgcolor: alpha("#fff", 0.24) },
            }}
          >
            {selectedFyLabel}
          </Button>
          <Menu
            anchorEl={fyAnchor}
            open={Boolean(fyAnchor)}
            onClose={() => setFyAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {financialYears.map((fy) => {
              const id = String(fy.financial_year_id || fy.year_id || fy.id || "");
              return (
                <MenuItem
                  key={id}
                  selected={id === String(selectedFinancialYearId)}
                  onClick={() => {
                    setSelectedFinancialYearId(id);
                    setFyAnchor(null);
                  }}
                  sx={{ fontWeight: id === String(selectedFinancialYearId) ? 800 : 600 }}
                >
                  {fy.financial_year_name || fy.name || id}
                </MenuItem>
              );
            })}
          </Menu>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDashboard}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              height: 36,
              borderRadius: "10px",
              color: "#fff",
              borderColor: alpha("#fff", 0.45),
              "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.08) },
            }}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<LinkIcon />}
            onClick={() => openMapDialog(null)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              height: 36,
              borderRadius: "10px",
              bgcolor: crmColors.energy.main,
              "&:hover": { bgcolor: crmColors.energy.dark },
            }}
          >
            Map referral
          </Button>
        </Stack>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
        {/* Month rail */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${crmColors.border.default}`,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2, pt: 1.5, pb: 1.25 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }} flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontWeight: 800, fontSize: 14 }}>Month</Typography>
              <Chip
                size="small"
                label="All months"
                clickable
                onClick={() => setSelectedMonth(null)}
                color={!selectedMonth ? "primary" : "default"}
                variant={!selectedMonth ? "filled" : "outlined"}
                sx={{ fontWeight: 700 }}
              />
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
                gap: 0.75,
              }}
            >
              {financialYearMonths.map((m) => {
                const selected =
                  selectedMonth &&
                  selectedMonth.monthNumber === m.monthNumber &&
                  selectedMonth.year === m.year;
                return (
                  <Button
                    key={`${m.year}-${m.monthNumber}`}
                    size="small"
                    onClick={() => setSelectedMonth(m)}
                    sx={{
                      minWidth: 0,
                      py: 0.85,
                      px: 0.5,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: selected ? 800 : 600,
                      fontSize: 12,
                      border: `1px solid ${
                        selected
                          ? crmColors.primary.main
                          : crmColors.border.default
                      }`,
                      bgcolor: selected
                        ? alpha(crmColors.primary.main, 0.12)
                        : "#fff",
                      color: selected
                        ? crmColors.primary.dark
                        : crmColors.text.primary,
                    }}
                  >
                    {MONTH_SHORT[m.monthNumber - 1]}
                  </Button>
                );
              })}
            </Box>
          </Box>
        </Card>

        {/* KPI */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 1.25,
          }}
        >
          <SummaryTile
            label="Registered (period)"
            value={summary.registered_schools}
            color={crmColors.primary.main}
            icon={<RegisteredIcon sx={{ fontSize: 20 }} />}
          />
          <SummaryTile
            label="Active billed"
            value={summary.active_schools}
            color={crmColors.success.main}
            icon={<ActiveIcon sx={{ fontSize: 20 }} />}
          />
          <SummaryTile
            label="Referring staff"
            value={summary.referring_staff}
            color={crmColors.warning.main}
            icon={<PersonIcon sx={{ fontSize: 20 }} />}
          />
          <SummaryTile
            label="Unmapped schools"
            value={summary.unmapped_schools}
            color={crmColors.error.main}
            icon={<LinkIcon sx={{ fontSize: 20 }} />}
          />
        </Box>

        <Card
          elevation={0}
          sx={{ borderRadius: 2.5, border: `1px solid ${crmColors.border.default}` }}
        >
          <Tabs
            value={selectedTab}
            onChange={(_, v) => setSelectedTab(v)}
            sx={{ borderBottom: 1, borderColor: "divider", px: 1 }}
          >
            <Tab label={`Registered schools (${filteredSchools.length})`} sx={{ textTransform: "none", fontWeight: 700 }} />
            <Tab label={`Referring staff (${filteredStaff.length})`} sx={{ textTransform: "none", fontWeight: 700 }} />
            <Tab label={`Map referral (${unmappedSchools.length})`} sx={{ textTransform: "none", fontWeight: 700 }} />
          </Tabs>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : null}

          {/* Schools */}
          {!loading && selectedTab === 0 && (
            <CardContent sx={{ p: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search school / code / referrer…"
                  value={schoolSearch}
                  onChange={(e) => setSchoolSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary", fontSize: 18 }} />,
                  }}
                  sx={{ flex: 1 }}
                />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Staff code</InputLabel>
                  <Select
                    label="Staff code"
                    value={selectedStaffCode}
                    onChange={(e) => setSelectedStaffCode(e.target.value)}
                  >
                    <MenuItem value="">All staff</MenuItem>
                    {staffOptions.map((s) => (
                      <MenuItem key={s.referal_code} value={s.referal_code}>
                        {s.staff_name} ({s.referal_code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>School</TableCell>
                      <TableCell>Referrer</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Registered</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSchools.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No schools registered by referral in this session/month.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSchools.map((row) => (
                        <TableRow key={row.school_code} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.school_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.school_code}
                              {row.admin_phone ? ` · ${row.admin_phone}` : ""}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.referrer_name || "—"}
                            </Typography>
                            {row.referrer_role ? (
                              <Typography variant="caption" color="text.secondary">
                                {row.referrer_role}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                                {row.referal_code}
                              </Typography>
                              <IconButton size="small" onClick={() => handleCopy(row.referal_code)}>
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {row.registered_date || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.billing_status || (row.is_active ? "Active" : "Registered")}
                              color={row.is_active ? "success" : "default"}
                              sx={{ fontWeight: 700, height: 22 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openMapDialog(row)}
                              sx={{ textTransform: "none", fontWeight: 700 }}
                            >
                              Remap
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          )}

          {/* Staff */}
          {!loading && selectedTab === 1 && (
            <CardContent sx={{ p: 2 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search staff / code…"
                value={staffSearch}
                onChange={(e) => setStaffSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary", fontSize: 18 }} />,
                }}
                sx={{ mb: 2 }}
              />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Staff</TableCell>
                      <TableCell>Referral code</TableCell>
                      <TableCell align="center">Registered</TableCell>
                      <TableCell align="center">Active</TableCell>
                      <TableCell align="center">Total referral</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStaff.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No referring staff with codes found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredStaff.map((row) => (
                        <TableRow key={`${row.staff_id || "x"}-${row.referal_code}`} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.staff_name || row.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[row.staff_role, row.staff_phone].filter(Boolean).join(" · ") || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 800 }}>
                                {row.referal_code}
                              </Typography>
                              <IconButton size="small" onClick={() => handleCopy(row.referal_code)}>
                                <CopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={row.registered_count ?? row.registered ?? 0}
                              sx={{
                                fontWeight: 800,
                                bgcolor: alpha(crmColors.primary.main, 0.12),
                                color: crmColors.primary.main,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={row.active_count ?? row.active ?? 0}
                              sx={{
                                fontWeight: 800,
                                bgcolor: alpha(crmColors.success.main, 0.12),
                                color: crmColors.success.main,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={row.total_referrals ?? row.total ?? 0}
                              sx={{
                                fontWeight: 800,
                                bgcolor: alpha(crmColors.warning.main, 0.14),
                                color: crmColors.warning.dark,
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                setSelectedStaffCode(row.referal_code);
                                setSelectedTab(0);
                              }}
                              sx={{ textTransform: "none", fontWeight: 700 }}
                            >
                              View schools
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                Registered = schools in selected session/month · Active = currently billed/grace · Total = all-time by code
              </Typography>
            </CardContent>
          )}

          {/* Map tab */}
          {!loading && selectedTab === 2 && (
            <CardContent sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Schools in this session/month with no referral code — map them to a staff code.
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={() => openMapDialog(null)}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Map referral
                </Button>
              </Stack>
              <TextField
                size="small"
                fullWidth
                placeholder="Search unmapped schools…"
                value={mapSearch}
                onChange={(e) => setMapSearch(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary", fontSize: 18 }} />,
                }}
              />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>School</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Registered</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mapCandidates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No unmapped schools in this period.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      mapCandidates.map((row) => (
                        <TableRow key={row.school_code} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {row.school_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.school_code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {[row.admin_name, row.admin_phone].filter(Boolean).join(" · ") || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {row.registered_date || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="contained"
                              disableElevation
                              onClick={() => openMapDialog(row)}
                              sx={{ textTransform: "none", fontWeight: 700 }}
                            >
                              Map
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          )}
        </Card>
      </Box>

      {/* Map dialog */}
      <Dialog open={mapOpen} onClose={() => !saving && setMapOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Map referral</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Assign a staff referral code when it was left blank or entered incorrectly.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {mapSchool?.school_code ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${crmColors.border.default}`,
                    bgcolor: alpha(crmColors.primary.main, 0.04),
                  }}
                >
                  <Typography sx={{ fontWeight: 800 }}>{mapSchool.school_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {mapSchool.school_code}
                    {mapSchool.referal_code
                      ? ` · current code ${mapSchool.referal_code}`
                      : " · no code"}
                  </Typography>
                </Box>
              ) : (
                <>
                  <TextField
                    size="small"
                    fullWidth
                    label="Search unmapped school"
                    value={mapSearch}
                    onChange={(e) => setMapSearch(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <FormControl fullWidth size="small">
                    <InputLabel>School</InputLabel>
                    <Select
                      label="School"
                      value={mapSchool?.school_code || ""}
                      onChange={(e) => {
                        const found = unmappedSchools.find(
                          (s) => s.school_code === e.target.value
                        );
                        setMapSchool(found || null);
                      }}
                    >
                      <MenuItem value="">Select school</MenuItem>
                      {mapCandidates.map((s) => (
                        <MenuItem key={s.school_code} value={s.school_code}>
                          {s.school_name} ({s.school_code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Referring staff / code</InputLabel>
                <Select
                  label="Referring staff / code"
                  value={mapCode}
                  onChange={(e) => setMapCode(e.target.value)}
                >
                  <MenuItem value="">Select code</MenuItem>
                  {staffOptions.map((s) => (
                    <MenuItem key={s.referal_code} value={s.referal_code}>
                      {s.staff_name} · {s.referal_code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Or type code"
                value={mapCode}
                onChange={(e) => setMapCode(e.target.value.toUpperCase())}
                helperText="Use staff code from tm_staff_master.referal_code"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMapOpen(false)} disabled={saving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMapReferral}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {saving ? "Saving…" : "Save mapping"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContentLayout>
  );
};

export default ReferralManagement;
